import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getPersonalFilteringOptions,
  getClearPersonalModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import AddOrEditPersonal from "./components/AddOrEditPersonal";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Personal() {
  const {
    PERSONAL_API,
    personalModel,
    setPersonalModel,
    personalData,
    config,
    pageIndexPersonal,
    setPageIndexPersonal,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    setCity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [personalDataFiltered, setPersonalDataFiltered] = useState(
    personalData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getPersonalFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const personalDataObj = personalData.Data.find(
        (personalObj) => personalObj.id === Number(id)
      );
      setPersonalModel(personalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setPersonalDataFiltered(personalData.Data);
  }, [personalData]);

  const validateOperationResult = (result, isDelete) => {
    setPersonalModel(getClearPersonalModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([PERSONAL_API, config]);
    if (isDelete) {
      if (pageIndexPersonal !== 0 && personalData.Data.length === 1) {
        setPageIndexPersonal(pageIndexPersonal - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${PERSONAL_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${PERSONAL_API}/${data.id}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${PERSONAL_API}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !personalModel?.IdentificationType ||
    !personalModel?.Identification ||
    personalModel?.Identification.replaceAll(" ", "").length <= 0 ||
    !personalModel?.LastName ||
    personalModel?.LastName.replaceAll(" ", "").length <= 0 ||
    !personalModel?.SurName ||
    personalModel?.SurName.replaceAll(" ", "").length <= 0 ||
    personalModel?.CityId <= 0 ||
    personalModel?.StateId <= 0;

  const getColumnTitles = () => {
    return [
      localize.identification_type,
      localize.identification,
      localize.surName,
      localize.lastName,
      localize.position,
      // localize.warehouse,
      localize.contractor,
      localize.phone_number,
      localize.email,
      localize.address,
      localize.department,
      localize.city,
      // localize.municipality,
      // localize.sidewalk,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.personal}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={PERSONAL_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="personal"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_personal}
          isEditForm
          onClickToSubmit={async () => {
            await handleUpdateRequest(personalModel);
            setShowEditModal(false);
            setCity(null);

            setPersonalModel(getClearPersonalModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setCity(null);

            setPersonalModel(getClearPersonalModel());
          }}
          Component={<AddOrEditPersonal />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.personal}
            subtitle={localize.personal_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityType="personal_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_personal}
                onClickToSubmit={async () => {
                  await handleCreateRequest(personalModel);
                  setShowAddModal(false);
                  setCity(null);
                  setPersonalModel(getClearPersonalModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setCity(null);
                  setPersonalModel(getClearPersonalModel());
                }}
                Component={<AddOrEditPersonal />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={personalModel.SurName}
        entityType={localize.personal}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setPersonalModel(getClearPersonalModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(personalModel.id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexPersonal}
            setPageIndex={setPageIndexPersonal}
            dataArray={personalData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          personalData.Data?.length !== 0
            ? personalDataFiltered.map((item) => ({
                key: item.id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setPersonalModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          ContractorId: item.Contractor?.Id ?? 0,
                          PositionId: item.Position?.Id ?? 0,
                          WarehouseId: item.Warehouse?.Id ?? 0,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setPersonalModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel data={item.IdentificationType} />,
                  <ValueOrNoDataLabel data={item.Identification} />,
                  <ValueOrNoDataLabel data={item.SurName} />,
                  <ValueOrNoDataLabel data={item.LastName} />,
                  <ValueOrNoDataLabel data={item.Position?.Name} />,
                  // <ValueOrNoDataLabel data={item.Warehouse?.Name} />,
                  <ValueOrNoDataLabel data={item.Contractor?.Name} />,
                  <ValueOrNoDataLabel data={item.PhoneNumber} />,
                  <ValueOrNoDataLabel data={item.EmailAddress} />,
                  <ValueOrNoDataLabel data={item.Address} />,
                  <ValueOrNoDataLabel data={item.State?.Name} />,
                  <ValueOrNoDataLabel data={item.City?.Name} />,
                  // <ValueOrNoDataLabel data={item.Municipality?.Name} />,
                  // <ValueOrNoDataLabel data={item.sidewalk?.Name} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  <ValueOrNoDataLabel data={item.CreatedBy} />,
                ],
              }))
            : [generateNoDataValues(14)]
        }
      />
    </div>
  );
}
